import { config } from '@/configs/api.config'
import { config as envConfig } from '@/configs/env.config'
import { IOrganization } from '@/models'

const POSTHOG_GROUP_TYPE = 'organization'
const BATCH_SIZE = 500
const LAST_SYNC_LS_KEY = 'posthog-last-sync'

type GroupIdentifyEvent = {
  event: '$groupidentify'
  distinct_id: string
  properties: {
    $group_type: string
    $group_key: string
    $group_set: Record<string, unknown>
  }
}

export type PostHogSyncResult = {
  ok: number
  failed: number
  totalBatches: number
  errors: string[]
}

export type PostHogSyncProgress = {
  completedBatches: number
  totalBatches: number
  ok: number
  failed: number
}

export type PostHogLastSync = {
  timestamp: string
  triggeredBy?: string
  result: PostHogSyncResult
  totalOrgs: number
}

export type PricingTierLookup = Record<
  string,
  { id?: string; name?: string } | undefined
>

export const POSTHOG_SYNCED_PROPERTIES = [
  'name',
  'status',
  'env',
  'pricing_tier_id',
  'pricing_tier_name',
  'subscription_status',
  'subscription_expiry_date',
  'company_size'
] as const

export const buildSyncedProperties = (
  org: IOrganization,
  pricingTierByOrgId?: PricingTierLookup
): Record<string, unknown> => {
  const tier = org.id ? pricingTierByOrgId?.[org.id] : undefined

  return {
    name: org.name,
    status: org.status,
    env: envConfig.STAGE,
    pricing_tier_id: tier?.id ?? org.pricing_tier_id,
    pricing_tier_name: tier?.name,
    subscription_status: org.subscription_status,
    subscription_expiry_date: org.subscription_expiry_date,
    company_size: org.company_size
  }
}

const toGroupIdentifyEvent = (
  org: IOrganization & { id: string },
  pricingTierByOrgId?: PricingTierLookup
): GroupIdentifyEvent => ({
  event: '$groupidentify',
  distinct_id: `admin-portal-sync:${org.id}`,
  properties: {
    $group_type: POSTHOG_GROUP_TYPE,
    $group_key: org.id,
    $group_set: buildSyncedProperties(org, pricingTierByOrgId)
  }
})

const chunk = <T>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = []

  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }

  return chunks
}

export const isPostHogConfigured = (): boolean =>
  Boolean(config.POSTHOG_PROJECT_TOKEN && config.POSTHOG_HOST)

export type PostHogPingResult =
  | { ok: true; status: number }
  | { ok: false; status?: number; error: string }

export const pingPostHog = async (): Promise<PostHogPingResult> => {
  if (!isPostHogConfigured()) {
    return { ok: false, error: 'PostHog is not configured for this stage' }
  }

  try {
    const response = await fetch(`${config.POSTHOG_HOST}/decide/?v=3`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: config.POSTHOG_PROJECT_TOKEN,
        distinct_id: 'admin-portal-health-check'
      })
    })

    if (!response.ok) {
      const body = await response.text().catch(() => '')

      return {
        ok: false,
        status: response.status,
        error: body
          ? `${response.status}: ${body.slice(0, 200)}`
          : `HTTP ${response.status}`
      }
    }

    return { ok: true, status: response.status }
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

export const getLastPostHogSync = (): PostHogLastSync | null => {
  try {
    const raw = localStorage.getItem(LAST_SYNC_LS_KEY)

    return raw ? (JSON.parse(raw) as PostHogLastSync) : null
  } catch {
    return null
  }
}

const setLastPostHogSync = (info: PostHogLastSync) => {
  try {
    localStorage.setItem(LAST_SYNC_LS_KEY, JSON.stringify(info))
  } catch (error) {
    console.error('Failed to persist last PostHog sync info', error)
  }
}

export const syncOrganizationsToPostHog = async (
  orgs: IOrganization[],
  options: {
    onProgress?: (progress: PostHogSyncProgress) => void
    triggeredBy?: string
    pricingTierByOrgId?: PricingTierLookup
  } = {}
): Promise<PostHogSyncResult> => {
  if (!isPostHogConfigured()) {
    throw new Error('PostHog is not configured for this stage')
  }

  const url = `${config.POSTHOG_HOST}/batch/`
  const projectToken = config.POSTHOG_PROJECT_TOKEN
  const events = orgs
    .filter((o): o is IOrganization & { id: string } => Boolean(o.id))
    .map((o) => toGroupIdentifyEvent(o, options.pricingTierByOrgId))
  const batches = chunk(events, BATCH_SIZE)

  let ok = 0
  let failed = 0
  const errors: string[] = []

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i]

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: projectToken, batch })
      })

      if (!response.ok) {
        const body = await response.text().catch(() => '')

        throw new Error(
          `PostHog responded ${response.status}${
            body ? `: ${body.slice(0, 200)}` : ''
          }`
        )
      }

      ok += batch.length
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)

      errors.push(`Batch ${i + 1}/${batches.length}: ${message}`)
      console.error('Failed to sync batch to PostHog', error)
      failed += batch.length
    }

    options.onProgress?.({
      completedBatches: i + 1,
      totalBatches: batches.length,
      ok,
      failed
    })
  }

  const result: PostHogSyncResult = {
    ok,
    failed,
    totalBatches: batches.length,
    errors
  }

  setLastPostHogSync({
    timestamp: new Date().toISOString(),
    triggeredBy: options.triggeredBy,
    result,
    totalOrgs: orgs.length
  })

  return result
}
